import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DropdownInputsSignalService } from './dropdown-inputs-signal.service';
import { Module } from '../interfaces/module.interface';
import { ModulesService } from './modules.service';
import { JwtService } from './jwt.service';

@Injectable({ providedIn: 'root' })
export class PermissionService {
    private API_USERS_URL = `${environment.apiBaseUrl}/User`;

    private perms$ = new BehaviorSubject<string[]>([]);

    constructor(
        private http: HttpClient,
        private signalService: DropdownInputsSignalService,
        private moduleService: ModulesService,
        private jwtService: JwtService
    ) {
        // Listen for permission invalidation events
        this.signalService.reload$.subscribe((signal) => {
            if (signal === 'invalidatePermissions') {
                const userId = this.jwtService.getUserId(sessionStorage.getItem('accessToken') || '');
                if (userId) {
                    this.loadPermissions(userId, false);
                }
            }
        });
    }

    loadPermissions = (userId: string, getCachedPermissions: boolean = true) => {
        if(getCachedPermissions) {
            let permissions = sessionStorage.getItem('permissions');
            if (permissions === "undefined") permissions = '[]';

            const prevPremissions = JSON.parse(permissions ?? '[]') as string[];

            this.setPermissions(prevPremissions);
            this.signalService.triggerReload('permissions');

            if (prevPremissions.length > 0) return;
        }

        this.http
            .get<string[]>(`${this.API_USERS_URL}/${userId}/Claims`)
            .subscribe({
                next: (list: any) => {
                    const perms: string[] = list.data.roles?.[0]?.claims.map((el: any) => el.value.toLowerCase()) ?? [];
                    this.fetchAllModules(perms);
                    this.signalService.triggerReload("permissions");
                },
                error: (error) => {
                    this.setPermissions([]);
                    this.signalService.triggerReload("permissions");
                }
            });
    }

    fetchAllModules(userPremssions: string[]) {
        this.moduleService.myModules()
            .subscribe({
                next: (res) => {
                    const modules: string[] = [];
                    (res.data as Module[] ?? []).forEach((el) => el.pages?.forEach((ele) =>
                        ele.actionList.forEach((action) => modules.push(action.permissionKey.toLowerCase()))
                    ));

                    // Filter user permissions that exist in modules
                    const result = userPremssions.filter(el => {
                        const permLower = el.toLowerCase();
                        // Check if permission exists in modules (exact match or case-insensitive)
                        return modules.some(mod => mod === permLower || mod.toLowerCase() === permLower);
                    });
                    
                    // Also keep permissions that match the pattern even if not in modules
                    // This handles cases where API returns permissions directly
                    userPremssions.forEach(perm => {
                        const permLower = perm.toLowerCase();
                        if (!result.includes(permLower) && permLower.startsWith('permissions.')) {
                            result.push(permLower);
                        }
                    });
                    
                    // If user has root_access, add it to the result
                    if (userPremssions.includes('root_access')) {
                        result.push('root_access');
                    }

                    this.setPermissions(result);
                    sessionStorage.setItem('permissions', JSON.stringify(result));

                    this.signalService.triggerReload('permissions');
                },
                error: (err) => {
                    console.log(err);
                    // Even on error, save permissions from API directly (for root_access and permissions.* pattern)
                    const allPerms = userPremssions.filter(perm => 
                        perm.toLowerCase().startsWith('permissions.') || perm === 'root_access'
                    );
                    this.setPermissions(allPerms);
                    sessionStorage.setItem('permissions', JSON.stringify(allPerms));
                    this.signalService.triggerReload('permissions');
                }
            });
    }


    setPermissions(perms: string[]) {
        this.perms$.next(perms);
    }

    getPermissions(): Observable<string[]> {
        return this.perms$.asObservable();
    }

    hasPermission(key: string): boolean {
        if(key === "allow") return true;
        
        // Check if user is admin by decoding the JWT token directly
        const token = sessionStorage.getItem('accessToken');
        if (token) {
            const user = this.jwtService.decodeToken(token);
            if (user?.is_admin === "True" || user?.is_admin === true) {
                return true;
            }
        }
        
        const permissions = this.perms$.getValue();
        
        // Check if user has root_access - grants all permissions
        if (permissions.includes('root_access')) {
            return true;
        }
        
        return permissions.includes(key.toLowerCase());
    }

    hasAny(keys: string[]): boolean {
        return keys.some(k => this.hasPermission(k));
    }
}
